import type { CompanyListItem } from "@/models/company.model";
import { dateDescNullsLastSortingFn } from "@/shared/utils/sorting/data-table-date-sorting";
import { DateCell } from "@spz/ui/components/table/cells/date-cell";
import { NumericCell } from "@spz/ui/components/table/cells/numeric-cell";
import { TextCell } from "@spz/ui/components/table/cells/text-cell";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CompanyDefaultOwnerSelect } from "../components/companies/company-default-owner-select";

export const useCompaniesColumns = () => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const columnHelper = createColumnHelper<CompanyListItem>();

    return [
      columnHelper.accessor("name", {
        header: t("company"),
        enableSorting: false,
        enableGlobalFilter: true,
        cell: ({ getValue }) => {
          const controlKey = getValue();
          return <TextCell value={t(controlKey)} />;
        },
      }),
      columnHelper.accessor("analysisCount", {
        header: t("analysis"),
        enableSorting: true,
        enableGlobalFilter: false,
        size: 100,
        cell: ({ getValue }) => {
          const analysisCount = getValue();
          const format = {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          };
          return <NumericCell value={analysisCount} format={format} />;
        },
      }),
      columnHelper.accessor("lastExecution", {
        header: t("last_execution"),
        size: 100,
        enableSorting: true,
        enableGlobalFilter: false,
        sortingFn: dateDescNullsLastSortingFn,
        cell: ({ getValue }) => (
          <DateCell value={getValue()} locale={i18n.language} />
        ),
      }),
      columnHelper.accessor("defaultOwner", {
        header: t("default_result_owner"),
        enableGlobalFilter: false,
        enableSorting: false,
        cell: ({ getValue, row }) => {
          const defaultOwnerId = getValue();
          const companyId = row.original.id;

          return (
            <CompanyDefaultOwnerSelect
              companyId={companyId}
              defaultOwnerId={defaultOwnerId}
            />
          );
        },
      }),
    ];
  }, [t, i18n]);
};
